// 관리자 — 신규 개설·폐업 치과기공소 (2026-09-03 대표 지시)
// 행안부 지방인허가 파일(치과기공소 전량, 매일 갱신)에서 개설·폐업이 최근인 곳을 자동 수집(newLabLeads).
// 관리자는 열람·처리 표시·메모만(rules). 데이터 생성은 서버(CF) 전용.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc, query, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../../firebase/config';
import { Factory, RefreshCw, Phone, MapPin, Check, Undo2 } from 'lucide-react';

const DAY = 86400000;
const toDate = (t) => t ? (typeof t.toDate === 'function' ? t.toDate() : new Date(t)) : null;
const fmt = (d) => d ? `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}` : '-';
const fmtTs = (t) => { const d = toDate(t); return d ? `${fmt(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '-'; };

function AdminNewLabs() {
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [tab, setTab] = useState('opened');          // opened | closed
    const [days, setDays] = useState(30);
    const [region, setRegion] = useState('');
    const [showHandled, setShowHandled] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState('');
    const [memoDraft, setMemoDraft] = useState({});

    const load = useCallback(async () => {
        setLoading(true); setLoadError('');
        try {
            const [snap, metaSnap] = await Promise.all([
                getDocs(query(collection(db, 'newLabLeads'), orderBy('lastSeenAt', 'desc'), limit(1500))),
                getDoc(doc(db, 'newLabLeadsMeta', 'status')),
            ]);
            setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setMeta(metaSnap.exists() ? metaSnap.data() : null);
        } catch (e) {
            console.error('[신규기공소] 목록 조회 실패:', e);
            setLoadError(e?.message || '목록을 불러오지 못했습니다');
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const runSync = async () => {
        if (syncing) return;
        setSyncing(true); setSyncMsg('행안부 인허가 파일(전국 기공소 전량)을 받는 중… (30초~1분)');
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'adminSyncNewLabs', { timeout: 540000 });
            const r = await fn({ days: 120 });
            const d = r.data || {};
            setSyncMsg(`완료 — 전국 ${d.rows ?? 0}곳 중 최근 120일 개설 ${d.opened ?? 0} · 폐업 ${d.closed ?? 0}`);
            await load();
        } catch (e) {
            console.error('[신규기공소] 수동 수집 실패:', e);
            setSyncMsg('실패: ' + (e?.message || e));
        } finally { setSyncing(false); }
    };

    const setHandled = async (row, handled) => {
        try {
            await updateDoc(doc(db, 'newLabLeads', row.id), {
                handled, handledAt: handled ? serverTimestamp() : null, handledBy: handled ? (auth.currentUser?.uid || null) : null,
            });
            setRows(prev => prev.map(r => r.id === row.id ? { ...r, handled } : r));
        } catch (e) { console.error('[신규기공소] 처리 표시 실패:', e); alert('저장 실패: ' + (e?.message || e)); }
    };
    const saveMemo = async (row) => {
        const memo = (memoDraft[row.id] ?? row.memo ?? '').trim();
        try {
            await updateDoc(doc(db, 'newLabLeads', row.id), { memo });
            setRows(prev => prev.map(r => r.id === row.id ? { ...r, memo } : r));
            setMemoDraft(prev => { const n = { ...prev }; delete n[row.id]; return n; });
        } catch (e) { console.error('[신규기공소] 메모 저장 실패:', e); alert('저장 실패: ' + (e?.message || e)); }
    };

    const filtered = useMemo(() => {
        const since = Date.now() - days * DAY;
        const q = region.trim();
        return rows.filter(r => {
            if (r.event !== tab) return false;
            const d = toDate(tab === 'opened' ? r.openedAt : r.closedAt);
            if (!d || d.getTime() < since) return false;
            if (!showHandled && r.handled) return false;
            if (q && !(`${r.addr} ${r.addrJibun} ${r.name}`).includes(q)) return false;
            return true;
        }).sort((a, b) => (toDate(tab === 'opened' ? b.openedAt : b.closedAt)?.getTime() || 0) - (toDate(tab === 'opened' ? a.openedAt : a.closedAt)?.getTime() || 0));
    }, [rows, tab, days, region, showHandled]);

    const handledCount = rows.filter(r => r.event === tab && r.handled).length;
    const dateOf = (r) => toDate(tab === 'opened' ? r.openedAt : r.closedAt);

    return (
        <div style={S.page}>
            <div style={S.head}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Factory size={22} color="#6366f1" />
                    <h1 style={S.h1}>신규 개설·폐업 기공소</h1>
                </div>
                <button onClick={runSync} disabled={syncing} style={{ ...S.btnP, opacity: syncing ? 0.6 : 1 }}>
                    <RefreshCw size={14} /> {syncing ? '가져오는 중…' : '지금 가져오기'}
                </button>
            </div>
            <div style={S.sub}>
                행정안전부 지방인허가 데이터(보건소 개설신고)에서 최근에 문을 연 기공소와 폐업한 기공소를 매일 아침 6:40에 자동으로 모읍니다.
                보건소 신고 기준이라 실제 영업 시작보다 빠르거나 늦을 수 있습니다.
                {meta && (
                    <span style={{ marginLeft: 8, color: '#94a3b8' }}>
                        · 마지막 수집 {fmtTs(meta.lastSyncAt)}{meta.running ? ' (진행 중)' : ''}
                        {meta.lastError ? <span style={{ color: '#dc2626' }}> · 오류: {meta.lastError}</span> : ''}
                    </span>
                )}
            </div>
            {syncMsg && <div style={S.notice}>{syncMsg}</div>}
            {loadError && <div style={{ ...S.notice, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>{loadError}</div>}

            <div style={S.toolbar}>
                <button onClick={() => setTab('opened')} style={{ ...S.tab, ...(tab === 'opened' ? S.tabOn : {}) }}>신규 개설</button>
                <button onClick={() => setTab('closed')} style={{ ...S.tab, ...(tab === 'closed' ? { ...S.tabOn, background: '#64748b', borderColor: '#64748b' } : {}) }}>폐업</button>
                <span style={{ width: 8 }} />
                <span style={S.label}>{tab === 'opened' ? '개설일' : '폐업일'}</span>
                {[7, 30, 60, 120].map(n => (
                    <button key={n} onClick={() => setDays(n)} style={{ ...S.chip, ...(days === n ? S.chipOn : {}) }}>최근 {n}일</button>
                ))}
                <input value={region} onChange={e => setRegion(e.target.value)} placeholder="지역·주소·기공소명 검색" style={S.input} />
                <label style={S.check}>
                    <input type="checkbox" checked={showHandled} onChange={e => setShowHandled(e.target.checked)} style={{ width: 'auto' }} />
                    처리한 곳도 보기 ({handledCount})
                </label>
                <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>{filtered.length}곳</span>
            </div>

            {loading ? (
                <div style={S.empty}>불러오는 중…</div>
            ) : filtered.length === 0 ? (
                <div style={S.empty}>
                    {rows.length === 0
                        ? '아직 수집된 기공소가 없습니다. [지금 가져오기]를 눌러 주세요.'
                        : '조건에 맞는 기공소가 없습니다. 기간을 늘리거나 검색어를 지워 보세요.'}
                </div>
            ) : (
                <div style={S.tableWrap}>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                <th style={S.th}>{tab === 'opened' ? '개설일' : '폐업일'}</th>
                                <th style={S.th}>기공소</th>
                                <th style={S.th}>전화</th>
                                <th style={S.th}>주소</th>
                                <th style={S.th}>메모</th>
                                <th style={S.th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(r => {
                                const d = dateOf(r);
                                const ageDays = d ? Math.floor((Date.now() - d.getTime()) / DAY) : null;
                                const draft = memoDraft[r.id];
                                return (
                                    <tr key={r.id} style={{ opacity: r.handled ? 0.55 : 1 }}>
                                        <td style={S.td}>
                                            <div style={{ fontWeight: 700 }}>{fmt(d)}</div>
                                            <div style={S.small}>{ageDays !== null ? `${ageDays}일 전` : ''}</div>
                                        </td>
                                        <td style={S.td}>
                                            <div style={{ fontWeight: 700, color: '#0f172a' }}>{r.name}</div>
                                            <div style={S.small}>{r.stateDetail || r.state}{tab === 'closed' && r.openedAt ? ` · 개설 ${fmt(toDate(r.openedAt))}` : ''}</div>
                                        </td>
                                        <td style={S.td}>
                                            {r.tel ? <a href={`tel:${r.tel}`} style={S.tel}><Phone size={12} /> {r.tel}</a> : <span style={S.small}>없음</span>}
                                        </td>
                                        <td style={S.td}>
                                            <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                                                <MapPin size={12} color="#94a3b8" style={{ marginTop: 3, flexShrink: 0 }} />
                                                <div>
                                                    <div>{r.addr || '-'}</div>
                                                    {r.postNo && <div style={S.small}>우 {r.postNo}</div>}
                                                </div>
                                            </div>
                                        </td>
                                        <td style={S.td}>
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <input value={draft ?? r.memo ?? ''} onChange={e => setMemoDraft(p => ({ ...p, [r.id]: e.target.value }))}
                                                    onKeyDown={e => { if (e.key === 'Enter') saveMemo(r); }}
                                                    placeholder="통화 결과 등" style={S.memo} />
                                                {draft !== undefined && draft !== (r.memo || '') && (
                                                    <button onClick={() => saveMemo(r)} style={S.btnMini}>저장</button>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                                            {r.handled ? (
                                                <button onClick={() => setHandled(r, false)} style={S.btnMini}><Undo2 size={12} /> 되돌리기</button>
                                            ) : (
                                                <button onClick={() => setHandled(r, true)} style={S.btnDone}><Check size={12} /> 처리함</button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

const S = {
    page: { padding: 20, maxWidth: 1200, margin: '0 auto' },
    head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
    h1: { fontSize: 20, fontWeight: 800, margin: 0, color: '#0f172a' },
    sub: { fontSize: 12.5, color: '#64748b', margin: '6px 0 14px', lineHeight: 1.6 },
    notice: { background: '#EEF2FF', border: '1px solid #C7D2FE', color: '#3730A3', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 },
    toolbar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
    tab: { padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
    tabOn: { background: '#6366f1', borderColor: '#6366f1', color: '#fff' },
    label: { fontSize: 12.5, fontWeight: 700, color: '#475569' },
    chip: { padding: '6px 11px', borderRadius: 999, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
    chipOn: { background: '#0f172a', borderColor: '#0f172a', color: '#fff' },
    input: { flex: 1, minWidth: 200, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' },
    check: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: '#475569', cursor: 'pointer' },
    empty: { padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13.5, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12 },
    tableWrap: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflowX: 'auto' },
    table: { width: '100%', minWidth: 880, borderCollapse: 'collapse' },
    th: { textAlign: 'left', fontSize: 11.5, fontWeight: 700, color: '#94a3b8', padding: '9px 12px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', whiteSpace: 'nowrap' },
    td: { padding: '10px 12px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
    small: { fontSize: 11.5, color: '#94a3b8', marginTop: 2 },
    tel: { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#4f46e5', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' },
    memo: { width: 180, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit' },
    btnP: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 15px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
    btnMini: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 9px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
    btnDone: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 11px', borderRadius: 6, border: 'none', background: '#0ca30c', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
};

export default AdminNewLabs;
